import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, STATUS_META, PRIORITY_META } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import IssueModal from "@/components/IssueModal";
import TextToTaskDialog from "@/components/TextToTaskDialog";
import { toast } from "sonner";
import {
  Building2, Plus, LogOut, ListTodo, CircleDashed, CheckCircle2, AlertTriangle,
  CalendarClock, Search, Paperclip, MapPin, User as UserIcon, Trash2, Pencil, Clock, Loader2,
  MessageSquareText,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="border border-zinc-200 bg-white rounded-md p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-[0.15em] font-bold text-zinc-400">{label}</span>
      <Icon className="h-4 w-4" style={{ color: accent }} />
    </div>
    <p className="mt-3 font-head text-3xl font-bold text-zinc-900">{value}</p>
  </div>
);

const Badge = ({ meta }) => (
  <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${meta.bg} ${meta.text}`}>
    {meta.label}
  </span>
);

const initials = (name) => (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [team, setTeam] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [textOpen, setTextOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fPriority, setFPriority] = useState("all");

  const load = useCallback(async () => {
    try {
      const [iss, st, tm] = await Promise.all([
        api.get("/issues"),
        api.get("/stats"),
        api.get("/team"),
      ]);
      setIssues(iss.data);
      setStats(st.data);
      setTeam(tm.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const openNew = () => { setEditing(null); setDraft(null); setModalOpen(true); };
  const openEdit = (issue) => { setEditing(issue); setDraft(null); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setDraft(null); };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this issue?")) return;
    try {
      await api.delete(`/issues/${id}`);
      toast.success("Issue deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const filtered = issues.filter((i) => {
    if (fStatus !== "all" && i.status !== fStatus) return false;
    if (fPriority !== "all" && i.priority !== fPriority) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(`${i.title} ${i.description} ${i.property_location} ${i.assignee_name || ""}`.toLowerCase().includes(s))) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-zinc-200 bg-white/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-zinc-900 flex items-center justify-center">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <span className="font-head font-bold text-lg tracking-tight">PropCollab</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm text-zinc-600">
              <Avatar className="h-7 w-7">
                <AvatarImage src={user?.picture} />
                <AvatarFallback className="text-xs bg-zinc-200">{initials(user?.name)}</AvatarFallback>
              </Avatar>
              <span data-testid="user-name">{user?.name}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-button" className="gap-2 text-zinc-600">
              <LogOut className="h-4 w-4" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-head text-3xl font-bold tracking-tight text-zinc-900">Maintenance Issues</h1>
            <p className="text-zinc-500 mt-1 text-sm">One shared list for your whole team.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => setTextOpen(true)} variant="outline" data-testid="text-to-task-button" className="gap-2 h-11">
              <MessageSquareText className="h-4 w-4" /> From Text
            </Button>
            <Button onClick={openNew} data-testid="new-issue-button" className="bg-zinc-900 hover:bg-zinc-800 gap-2 h-11">
              <Plus className="h-4 w-4" /> New Issue
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6">
          <StatCard testid="stat-total" icon={ListTodo} label="Total" value={stats.total ?? 0} accent="#18181B" />
          <StatCard testid="stat-open" icon={CircleDashed} label="Open" value={stats.open ?? 0} accent="#2563EB" />
          <StatCard testid="stat-progress" icon={Clock} label="In Progress" value={stats.in_progress ?? 0} accent="#EA580C" />
          <StatCard testid="stat-closed" icon={CheckCircle2} label="Closed" value={stats.closed ?? 0} accent="#16A34A" />
          <StatCard testid="stat-high" icon={AlertTriangle} label="High Prio" value={stats.high_priority ?? 0} accent="#DC2626" />
          <StatCard testid="stat-overdue" icon={CalendarClock} label="Overdue" value={stats.overdue ?? 0} accent="#DC2626" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mt-8">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search issues, properties, people…"
              className="pl-9 bg-white"
            />
          </div>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-[150px] bg-white" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fPriority} onValueChange={setFPriority}>
            <SelectTrigger className="w-[150px] bg-white" data-testid="filter-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              {Object.entries(PRIORITY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        <div className="mt-6 space-y-3" data-testid="issue-list">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-zinc-300 rounded-md py-20 text-center" data-testid="empty-state">
              <ListTodo className="h-8 w-8 mx-auto text-zinc-300" />
              <p className="mt-3 text-zinc-500">No issues yet. Create your first maintenance issue.</p>
              <Button onClick={openNew} className="mt-4 bg-zinc-900 hover:bg-zinc-800 gap-2"><Plus className="h-4 w-4" /> New Issue</Button>
            </div>
          ) : (
            filtered.map((issue) => {
              const sm = STATUS_META[issue.status] || STATUS_META.open;
              const pm = PRIORITY_META[issue.priority] || PRIORITY_META.medium;
              const overdue = issue.due_date && issue.due_date < today && issue.status !== "closed";
              return (
                <div
                  key={issue.id}
                  onClick={() => openEdit(issue)}
                  data-testid="issue-card"
                  className="group border border-zinc-200 bg-white rounded-md p-4 shadow-sm hover:border-zinc-400 hover:shadow-md transition-all duration-200 cursor-pointer flex gap-4"
                >
                  <div className={`w-1 rounded-full ${sm.dot} shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-head font-semibold text-zinc-900 truncate">{issue.title}</h3>
                        {issue.description && <p className="text-sm text-zinc-500 mt-0.5 line-clamp-1">{issue.description}</p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); openEdit(issue); }} data-testid="edit-issue-button" className="p-1.5 text-zinc-400 hover:text-zinc-900 rounded hover:bg-zinc-100">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button onClick={(e) => del(issue.id, e)} data-testid="delete-issue-button" className="p-1.5 text-zinc-400 hover:text-red-600 rounded hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3 text-xs text-zinc-500">
                      <Badge meta={sm} />
                      <Badge meta={pm} />
                      {issue.property_location && (
                        <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{issue.property_location}</span>
                      )}
                      {issue.assignee_name && (
                        <span className="inline-flex items-center gap-1"><UserIcon className="h-3.5 w-3.5" />{issue.assignee_name}</span>
                      )}
                      {issue.due_date && (
                        <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-600 font-semibold" : ""}`}>
                          <CalendarClock className="h-3.5 w-3.5" />{issue.due_date}{overdue ? " · overdue" : ""}
                        </span>
                      )}
                      {issue.attachments?.length > 0 && (
                        <span className="inline-flex items-center gap-1"><Paperclip className="h-3.5 w-3.5" />{issue.attachments.length}</span>
                      )}
                      {(issue.source === "sms" || issue.source === "text") && (
                        <span className="inline-flex items-center gap-1 text-zinc-500" data-testid="issue-source-badge">
                          <MessageSquareText className="h-3.5 w-3.5" />
                          {issue.source === "sms" ? "via SMS" : "from text"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </main>

      <IssueModal
        open={modalOpen}
        onClose={closeModal}
        issue={editing}
        initialDraft={draft}
        team={team}
        onSaved={load}
      />
      <TextToTaskDialog open={textOpen} onClose={() => setTextOpen(false)} onCreated={load} />
    </div>
  );
}
